/**
 * Shareable-card builders — OpenGraph / Twitter meta + schema.org JSON-LD.
 *
 * Wingspan cut rank 9 (WEDGE_ROADMAP §0.0) / FINAL_STATE §7 catalog surface +
 * §2 room 8 (Curator read-only half): the marketplace "front door" that search
 * engines and shared links walk through without an account. These are PURE
 * functions over plain data (no DOM, no browser APIs) so the identical logic
 * feeds BOTH the runtime client head manager (SPA nav) AND the crawler-facing
 * Cloudflare Pages Functions that server-render the head into the shell.
 */

/** A single <meta> tag: exactly one of `property` (OG) or `name` (Twitter/std). */
export interface MetaTag {
  property?: string;
  name?: string;
  content: string;
}

/** Absolute site origin (no trailing slash), e.g. https://xicocity.com. */
export const SITE_ORIGIN = 'https://xicocity.com';
/** API origin for stable, crawler-fetchable media URLs (cover/avatar 302s). */
export const API_ORIGIN = 'https://api.xicocity.com';
const SITE_NAME = 'DJMEXXICO';

/** Escape a string for safe interpolation into HTML text / attribute content. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** ms → ISO-8601 duration (PT#M#S), or null when unknown. */
export function isoDuration(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return null;
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `PT${m}M${s}S`;
}

/** cents → major-unit string with 2 decimals, e.g. 2500 → "25.00". */
export function priceMajor(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** Truncate for a card description (OG descriptions clip ~200 chars). */
export function clampDescription(text: string | null | undefined, max = 200): string {
  const s = (text ?? '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1).trimEnd()}…`;
}

/** Absolute URL for a listing's cover image (stable endpoint; 302 → presigned). */
export function listingImageUrl(listingId: string): string {
  return `${API_ORIGIN}/v1/listings/${listingId}/cover`;
}

/** Absolute URL for a creator avatar (stable endpoint; 302 → presigned). */
export function creatorAvatarUrl(creatorId: string): string {
  return `${API_ORIGIN}/v1/creators/${creatorId}/avatar`;
}

// ── Listing card ────────────────────────────────────────────────────────────
export interface ListingCardData {
  id: string;
  title: string;
  description?: string | null;
  price_cents: number;
  price_currency: string;
  license_type?: string;
  creator?: { id: string; display_name: string; handle?: string } | null;
  asset?: { bpm?: number | null; key_camelot?: string | null; duration_ms?: number | null } | null;
  /** Whether the asset has cover art; when false og:image is omitted. */
  has_image?: boolean;
}

/** Human card blurb: license + bpm/key facets fall back to the description. */
export function listingCardDescription(l: ListingCardData): string {
  const bits: string[] = [];
  if (l.asset?.bpm != null) bits.push(`${Math.round(l.asset.bpm)} BPM`);
  if (l.asset?.key_camelot) bits.push(`Key ${l.asset.key_camelot}`);
  const facets = bits.join(' · ');
  const base = clampDescription(l.description, 160);
  if (facets && base) return `${facets} — ${base}`;
  return facets || base || `${SITE_NAME} pack`;
}

/** OG + Twitter meta tags for a single listing (music.song card). */
export function buildListingMeta(l: ListingCardData): MetaTag[] {
  const url = `${SITE_ORIGIN}/market/${l.id}`;
  const title = `${l.title} — ${SITE_NAME}`;
  const description = listingCardDescription(l);
  const image = l.has_image === false ? null : listingImageUrl(l.id);
  const tags: MetaTag[] = [
    { property: 'og:type', content: 'music.song' },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: url },
    { name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'description', content: description },
  ];
  if (image) {
    tags.push({ property: 'og:image', content: image });
    tags.push({ name: 'twitter:image', content: image });
  }
  if (l.creator?.display_name) {
    tags.push({ property: 'music:musician', content: l.creator.display_name });
  }
  return tags;
}

/** schema.org Product+MusicRecording JSON-LD with an Offer (price/availability). */
export function buildListingJsonLd(l: ListingCardData): Record<string, unknown> {
  const url = `${SITE_ORIGIN}/market/${l.id}`;
  const node: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': ['Product', 'MusicRecording'],
    name: l.title,
    url,
    description: clampDescription(l.description, 300) || listingCardDescription(l),
    offers: {
      '@type': 'Offer',
      price: priceMajor(l.price_cents),
      priceCurrency: l.price_currency,
      availability: 'https://schema.org/InStock',
      url,
    },
  };
  if (l.has_image !== false) node.image = listingImageUrl(l.id);
  const dur = isoDuration(l.asset?.duration_ms);
  if (dur) node.duration = dur;
  if (l.creator?.display_name) {
    node.byArtist = {
      '@type': 'MusicGroup',
      name: l.creator.display_name,
      ...(l.creator.id ? { url: `${SITE_ORIGIN}/u/${l.creator.id}` } : {}),
    };
  }
  return node;
}

// ── Creator storefront ──────────────────────────────────────────────────────
export interface StorefrontData {
  creator: { id: string; handle: string; display_name: string; has_avatar?: boolean };
  items: ListingCardData[];
}

/**
 * Path for a creator's public storefront URL. Keyed by the creator's id
 * (users.id, the value carried as ListingSummary.creator_id) — the storefront
 * API endpoint resolves `:id` as that uuid. `handle` is display-only for now;
 * when human handles exist the endpoint + this path can switch to them.
 */
export function storefrontPath(creator: { id: string }): string {
  return `/u/${creator.id}`;
}

/** OG + Twitter meta for a creator storefront (profile card). */
export function buildStorefrontMeta(s: StorefrontData): MetaTag[] {
  const url = `${SITE_ORIGIN}${storefrontPath(s.creator)}`;
  const title = `${s.creator.display_name} — ${SITE_NAME}`;
  const n = s.items.length;
  const description =
    n > 0
      ? `${s.creator.display_name} on ${SITE_NAME} — ${n} pack${n === 1 ? '' : 's'} ready for your next session.`
      : `${s.creator.display_name} on ${SITE_NAME}.`;
  const image = s.creator.has_avatar ? creatorAvatarUrl(s.creator.id) : null;
  const tags: MetaTag[] = [
    { property: 'og:type', content: 'profile' },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: url },
    { property: 'profile:username', content: s.creator.handle },
    { name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'description', content: description },
  ];
  if (image) {
    tags.push({ property: 'og:image', content: image });
    tags.push({ name: 'twitter:image', content: image });
  }
  return tags;
}

/** schema.org MusicGroup JSON-LD with the creator's tracks (their listings). */
export function buildStorefrontJsonLd(s: StorefrontData): Record<string, unknown> {
  const url = `${SITE_ORIGIN}${storefrontPath(s.creator)}`;
  const node: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'MusicGroup',
    name: s.creator.display_name,
    url,
    track: s.items.slice(0, 25).map((l) => ({
      '@type': 'MusicRecording',
      name: l.title,
      url: `${SITE_ORIGIN}/market/${l.id}`,
      offers: {
        '@type': 'Offer',
        price: priceMajor(l.price_cents),
        priceCurrency: l.price_currency,
        availability: 'https://schema.org/InStock',
      },
    })),
  };
  if (s.creator.has_avatar) node.image = creatorAvatarUrl(s.creator.id);
  return node;
}

/** Serialize meta tags + a JSON-LD node into an HTML fragment (no <title>). */
export function renderMetaFragment(tags: MetaTag[], jsonLd: Record<string, unknown>): string {
  const metaHtml = tags
    .map((tag) => {
      const key = tag.property ? `property="${escapeHtml(tag.property)}"` : `name="${escapeHtml(tag.name ?? '')}"`;
      return `<meta ${key} content="${escapeHtml(tag.content)}" />`;
    })
    .join('');
  // JSON-LD is embedded in a script element; escape the closing-tag sequence.
  const ld = JSON.stringify(jsonLd).replace(/</g, '\\u003c');
  return `${metaHtml}<script type="application/ld+json">${ld}</script>`;
}

/** Serialize <title> + meta tags + a JSON-LD node into an HTML head fragment. */
export function renderHeadHtml(pageTitle: string, tags: MetaTag[], jsonLd: Record<string, unknown>): string {
  return `<title>${escapeHtml(pageTitle)}</title>${renderMetaFragment(tags, jsonLd)}`;
}
